import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { DetailsLayout } from "../components/bifrost"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Grid, Box, Tooltip, Button, Typography } from "@material-ui/core"
import { LabelValue, CircularProgressView } from "../components/bifrost"
import { getKbMakingDetailsRequest } from "../actions/bifrostActions"
import WithDrawer from "../components/common/drawer/withDrawer"
import { useTranslation } from "react-i18next"
import { getFormattedTime } from "../utils/helperFunctions"
import { Skeleton } from "@material-ui/lab"

const useStyles = makeStyles((theme) => ({
	jobDetails: {
		"& .MuiChip-root": {
			borderRadius: "4px",
		},
	},
	actionDiv: {
		display: "flex",
		justifyContent: "space-between",
	},
	primaryBtn: {
		textTransform: "initial",
		fontSize: theme.typography.fontSize,
		padding: "8px 12px",
		color: theme.palette.primary.dark,
	},
	skeleton: {
		marginTop: "32px",
		height: "200px",
		width: "200px",
	},
	chartTitle: {
		padding: "48px 0 16px 0",
	},
	kbName: { fontStyle: "italic", fontWeight: "700", color: theme.palette.text.primary },
}))
const PageKbMakingDetails = () => {
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const classes = useStyles()
	const history = useHistory()
	const { caseId, jobId } = useParams()
	const theme = useTheme()

	const kbMakingDetails = useSelector((state) => state.bifrostKbMakingDetails.kbMakingDetails)
	const isPageLoading = useSelector((state) => state.bifrostKbMakingDetails.isLoading)

	useEffect(() => {
		dispatch(getKbMakingDetailsRequest(jobId))
	}, [])

	const getActionItems = () => {
		return (
			<div className={classes.actionDiv}>
				<Button
					onClick={() => {
						history.push(`/bifrost/case/${caseId}`)
					}}
					className={classes.primaryBtn}
				>
					{t("bifrost.buttonTexts.back")}
				</Button>
			</div>
		)
	}

	const getCircularProgressChart = () => {
		return (
			<>
				<div style={{ display: "flex", justifyContent: "space-between", width: "50%" }}>
					<Typography className={classes.chartTitle}>{t("bifrost.kb_making.executionStatus")}</Typography>
				</div>
				<Box
					style={{
						width: "100%",
						background: theme.palette.common.white,
						border: `solid 1px ${theme.palette.grey.D400}`,
						borderRadius: "5px",
						height: "100%",
						padding: "32px",
					}}
				>
					<Typography variant="body2" component="p">
						{t("bifrost.kb_making.percentageExecutionSteps")}
					</Typography>
					<Grid container direction="row" xs={12} justifyContent="space-around">
						{!isPageLoading ? (
							<CircularProgressView percent={kbMakingDetails?.count} label={t("bifrost.kb_making.count")} />
						) : (
							<Skeleton animation="wave" variant="circle" className={classes.skeleton} />
						)}
						{!isPageLoading ? (
							<CircularProgressView percent={kbMakingDetails?.context} label={t("bifrost.kb_making.context")} />
						) : (
							<Skeleton animation="wave" variant="circle" className={classes.skeleton} />
						)}
						{!isPageLoading ? (
							<CircularProgressView percent={kbMakingDetails?.att_count} label={t("bifrost.kb_making.attractionCount")} />
						) : (
							<Skeleton animation="wave" variant="circle" className={classes.skeleton} />
						)}
						{!isPageLoading ? (
							<CircularProgressView percent={kbMakingDetails?.att} label={t("bifrost.kb_making.attraction")} />
						) : (
							<Skeleton animation="wave" variant="circle" className={classes.skeleton} />
						)}
						{!isPageLoading ? (
							<CircularProgressView percent={kbMakingDetails?.att_inverse} label={t("bifrost.kb_making.attractionInverse")} />
						) : (
							<Skeleton animation="wave" variant="circle" className={classes.skeleton} />
						)}
					</Grid>
				</Box>
			</>
		)
	}
	return (
		<div data-cy="kb-making-details" className={classes.jobDetails}>
			<DetailsLayout
				loading={isPageLoading}
				titleProps={{
					title: t("bifrost.titles.kbMakingbDetails"),
					name: kbMakingDetails?.name,
					status: kbMakingDetails?.status,
				}}
				actionItems={getActionItems()}
				listView={getCircularProgressChart()}
				showActionsView={false}
			>
				<>
					<Grid item xs={8}>
						<LabelValue
							label={t("bifrost.metadataKeys.kbName")}
							value={kbMakingDetails?.kb_making_config?.kb_name}
							loading={isPageLoading}
							valueStyle={classes.kbName}
						/>
					</Grid>
					<Grid item xs={4}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={6}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggerDate")}
									value={!!kbMakingDetails?.create_time ? new Date(kbMakingDetails?.create_time).toLocaleString() : "—"}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={6}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalTime")}
									value={kbMakingDetails?.total_time === 0 ? t("bifrost.metadataKeys.calculating") : getFormattedTime(kbMakingDetails?.total_time)}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={8}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={4}>
								<LabelValue
									label={t("bifrost.metadataKeys.documentsUsed")}
									value={kbMakingDetails.selected_documents_count}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={4}>
								<LabelValue
									label={t("bifrost.metadataKeys.noOfAvailableDocs")}
									value={kbMakingDetails?.total_documents_count}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={4}>
						<LabelValue
							label={t("bifrost.metadataKeys.jobTriggeredBy")}
							value={kbMakingDetails?.created_by_username}
							loading={isPageLoading}
						/>
					</Grid>
					<Grid item xs={8}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={4}>
								<LabelValue
									label={t("bifrost.metadataKeys.includeNewDocsOnly")}
									value={
										kbMakingDetails?.kb_making_config?.include_new_documents_only
											? t("bifrost.metadataKeys.yes")
											: t("bifrost.metadataKeys.no")
									}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={4}>
								<LabelValue
									label={t("bifrost.metadataKeys.includeDuplicateDocs")}
									value={
										kbMakingDetails?.kb_making_config?.include_duplicate_documents
											? t("bifrost.metadataKeys.yes")
											: t("bifrost.metadataKeys.no")
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={4}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.lower_n")}
									value={kbMakingDetails?.kb_making_config?.lower_n}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.higher_n")}
									value={kbMakingDetails?.kb_making_config?.higher_n}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.max_ngram")}
									value={kbMakingDetails?.kb_making_config?.count_config?.max_ngram}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.min_freq")}
									value={kbMakingDetails?.kb_making_config?.count_config?.min_frequency}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
				</>
			</DetailsLayout>
		</div>
	)
}

export default WithDrawer(PageKbMakingDetails)
